/**
 * product-links.ts
 *
 * Erkennt Dekalin-Produkte im Avatar-Transkriptionstext per Name-Matching.
 * Zeigt Produktkarten im Companion Panel an.
 *
 * Seit v3.4: Keine [PRODUKT:key]-Marker mehr im Prompt — stattdessen
 * werden Produktnamen direkt im Text erkannt (client-seitig).
 *
 * SDK: @heygen/liveavatar-web-sdk v0.0.9
 */

// ============================================================
// Types
// ============================================================

export interface ProductCard {
  key: string;
  name: string;
  url: string;
  category: 'produkt' | 'blog' | 'video';
  description?: string;
}

export interface ParseResult {
  cleanText: string;
  products: ProductCard[];
  blogs: ProductCard[];
  videos: ProductCard[];
  hasHandoff: boolean;
}

// ============================================================
// URL-Mapping — Echte dekalin.de URLs (gecrawlt 2026-03-17)
// URL-Pattern: /produkt/{woocommerce_id}/
// SSOT: 05-wissensbasis/dekalin-url-mapping.md
// ============================================================

export const PRODUCT_URLS: Record<string, { name: string; url: string; description?: string }> = {
  // ── DEKASYL MS-Polymer-Serie ──────────────────────────────
  // Config v3 generische Keys → leiten auf grau-Variante (häufigste)
  'ms-1':           { name: 'DEKASYL MS-1', url: 'https://dekalin.de/produkt/599/', description: 'Universeller Dicht-/Klebstoff, lösemittel-/isocyanatfrei' },
  'ms-1-weiss':     { name: 'DEKASYL MS-1 weiß', url: 'https://dekalin.de/produkt/598/', description: 'Elastischer Dicht-/Klebstoff, breites Haftspektrum ohne Primer' },
  'ms-1-grau':      { name: 'DEKASYL MS-1 grau', url: 'https://dekalin.de/produkt/599/', description: 'Universeller Dicht-/Klebstoff für Fahrzeug-, Bus-, Caravanbau' },
  'ms-1-schwarz':   { name: 'DEKASYL MS-1 schwarz', url: 'https://dekalin.de/produkt/600/', description: 'Universeller Dicht-/Klebstoff, lösemittel-/isocyanat-/silikonfrei' },
  'ms-2':           { name: 'DEKASYL MS-2', url: 'https://dekalin.de/produkt/553/', description: 'Hybrid-Kleb-/Dichtstoff, Kompromiss Klebkraft & Dichtung' },
  'ms-2-weiss':     { name: 'DEKASYL MS-2 weiß', url: 'https://dekalin.de/produkt/584/', description: 'Elastischer Kleb-/Dichtstoff mit hoher Haftstärke' },
  'ms-2-grau':      { name: 'DEKASYL MS-2 grau', url: 'https://dekalin.de/produkt/553/', description: 'Hybrid-Kleb-/Dichtstoff für anspruchsvolle Anwendungen' },
  'ms-2-schwarz':   { name: 'DEKASYL MS-2 schwarz', url: 'https://dekalin.de/produkt/554/', description: 'Kleb-/Dichtstoff mit Elastizität und starker Haftung' },
  'ms-5':           { name: 'DEKASYL MS-5', url: 'https://dekalin.de/produkt/556/', description: 'High-Tack Klebstoff, hohe Anfangsfestigkeit' },
  'ms-5-weiss':     { name: 'DEKASYL MS-5 weiß', url: 'https://dekalin.de/produkt/557/', description: 'MS-Polymer Klebstoff mit hoher Haftwirkung' },
  'ms-5-schwarz':   { name: 'DEKASYL MS-5 schwarz', url: 'https://dekalin.de/produkt/556/', description: 'MS-Polymer Klebstoff, hohe Anfangsfestigkeit' },
  'ms-6-weiss':     { name: 'DEKASYL MS-6 weiß', url: 'https://dekalin.de/produkt/641/', description: 'Universaler Dicht-/Klebstoff für Caravan-/Reisemobilbau' },
  'ms-6-schwarz':   { name: 'DEKASYL MS-6 schwarz', url: 'https://dekalin.de/produkt/657/', description: 'MS-Polymer Dicht-/Klebstoff, lösemittel-/isocyanatfrei' },
  'ms-7':           { name: 'DEKASYL MS-7 transparent', url: 'https://dekalin.de/produkt/589/', description: 'Elastischer, transparenter Dichtstoff, haftet auf Plexiglas' },
  'ms-7-transparent': { name: 'DEKASYL MS-7 transparent', url: 'https://dekalin.de/produkt/589/', description: 'Transparenter Dichtstoff für Plexiglas, Emaille, lackiertes Holz' },
  'ms-8-schwarz':   { name: 'DEKASYL MS-8 schwarz', url: 'https://dekalin.de/produkt/639/', description: 'Hochmoduliger Scheibenklebstoff, sehr hohe Anfangsfestigkeit' },
  'dekasyl-240-grau': { name: 'DEKASYL 240 grau', url: 'https://dekalin.de/produkt/622/', description: '2K elastischer Klebstoff, schnelle Aushärtung' },

  // ── Polyurethan-Serie ─────────────────────────────────────
  'dekapur-2k-6000': { name: 'DEKAPUR 2K-6000 A+B', url: 'https://dekalin.de/produkt/577/', description: '2K-PU Klebstoff für Seitenwandinstandsetzung und Verblechung' },
  'dekapur-2k-90':   { name: 'DEKAPUR 2K-90', url: 'https://dekalin.de/produkt/551/', description: 'Schnell aushärtender 2K-PU Kleber für strukturelle Reparaturen' },
  // PU-289, PU-292, PU-293: nicht auf dekalin.de (B2B-only oder ausgelaufen)
  'pu-289': { name: 'PU-289', url: 'https://dekalin.de/produkte/', description: 'Wasserhärtend – nicht im Webshop (B2B)' },
  'pu-292': { name: 'PU-292', url: 'https://dekalin.de/produkte/', description: 'Hohe Anfangshaftung – nicht im Webshop (B2B)' },
  'pu-293': { name: 'PU-293', url: 'https://dekalin.de/produkte/', description: 'Besondere Elastizität – nicht im Webshop (B2B)' },

  // ── DEKASEAL Dichtstoffe ──────────────────────────────────
  'dekaseal-8936':              { name: 'DEKASEAL 8936', url: 'https://dekalin.de/produkt/611/', description: 'Abtupfbare Dichtungsmasse, dauerhaft klebrig' },
  'dekaseal-8936-hellgrau':     { name: 'DEKASEAL 8936 hellgrau', url: 'https://dekalin.de/produkt/541/', description: 'Abtupfbare Dichtungsmasse, UV-beständig' },
  'dekaseal-8936-schwarz':      { name: 'DEKASEAL 8936 schwarz', url: 'https://dekalin.de/produkt/611/', description: 'Abtupfbare Dichtungsmasse auf Polyisobutylen-Basis' },
  'dekaseal-8936-anthrazit':    { name: 'DEKASEAL 8936 anthrazit', url: 'https://dekalin.de/produkt/661/', description: 'Abtupfbare Dichtungsmasse, UV-beständig' },
  'dekaseal-1512':              { name: 'DEKASEAL 1512', url: 'https://dekalin.de/produkt/585/', description: 'Überlackierbarer Butyl-Dichtstoff mit Hautbildung' },
  'dekaseal-1512-cremeweiss':   { name: 'DEKASEAL 1512 cremeweiß', url: 'https://dekalin.de/produkt/585/', description: 'Butyl-Dichtstoff für plastische Abdichtungen' },
  'dekaseal-1512-schwarz':      { name: 'DEKASEAL 1512 schwarz', url: 'https://dekalin.de/produkt/607/', description: 'Butyl-Dichtstoff, überlackierbar, standfest' },
  'dekaseal-8869':              { name: 'DEKASEAL 8869 Butyl-Mastic', url: 'https://dekalin.de/produkt/615/', description: 'Butyl-Dichtungsmasse, schnellhärtend' },
  'butyl-tape':                 { name: 'DEKASEAL Butyl-Tape', url: 'https://dekalin.de/produkt/508/', description: 'Abdichtmaterial mit hoher Beständigkeit' },
  'dekaplast-7303':             { name: 'DEKAPLAST 7303 grau', url: 'https://dekalin.de/produkt/116/', description: 'Hochleistungs-Mehrzweck-Butylband' },

  // ── AL-Serie: nicht auf Website (B2B-only) ────────────────
  'al-101':    { name: 'AL-101', url: 'https://dekalin.de/produkte/', description: 'Polyurethan, vielseitig – nicht im Webshop (B2B)' },
  'al-102-ms': { name: 'AL-102-MS', url: 'https://dekalin.de/produkte/', description: 'Monomer-Silikon – nicht im Webshop (B2B)' },
  'al-103':    { name: 'AL-103', url: 'https://dekalin.de/produkte/', description: 'Silikon-Dichtstoff – nicht im Webshop (B2B)' },
  'al-103-ms': { name: 'AL-103-MS', url: 'https://dekalin.de/produkte/', description: 'Monomer-Silikon, Alu-Fugen – nicht im Webshop (B2B)' },
  'al-104-ms': { name: 'AL-104-MS', url: 'https://dekalin.de/produkte/', description: 'Monomer-Silikon-Klebstoff – nicht im Webshop (B2B)' },
  'al-104-ls': { name: 'AL-104-LS', url: 'https://dekalin.de/produkte/', description: 'Polychloropren – nicht im Webshop (B2B)' },

  // ── Weitere Spezialisten ──────────────────────────────────
  'pvc-72':         { name: 'PVC-72', url: 'https://dekalin.de/produkte/', description: 'Flexibles/hartes PVC – nicht im Webshop (B2B)' },
  'cr-77':          { name: 'CR-77', url: 'https://dekalin.de/produkte/', description: 'Schnelle Verfestigung – nicht im Webshop (B2B)' },
  'ps-32':          { name: 'PS-32', url: 'https://dekalin.de/produkte/', description: 'Polystyrol – nicht im Webshop (B2B)' },
  'plastigum-77-tf': { name: 'DEKAPREN Plastigum 77 TF', url: 'https://dekalin.de/produkt/227/', description: 'Spezial-PVC-Kleber, alterungsbeständig' },
  'dekapren-3649':  { name: 'DEKAPREN 3649/2', url: 'https://dekalin.de/produkt/61/', description: 'Kontaktklebstoff für Gummi, Leder, Kunstleder, Metall' },

  // ── Schutz & Reinigung ────────────────────────────────────
  'dekaphon-9735':   { name: 'DEKAPHON 9735', url: 'https://dekalin.de/produkt/548/', description: 'Sprühbarer Unterbodenschutz, antikorrosiv' },
  'dekaphon-958':    { name: 'DEKAPHON 958', url: 'https://dekalin.de/produkt/593/', description: 'Wasserbasierter Unterbodenschutz, überlackierbar' },
  'dekaphon-pro-wax': { name: 'DEKAPHON Pro-Wax', url: 'https://dekalin.de/produkt/606/', description: 'Spritzbarer Korrosionsschutz, Hohlraumversiegelung' },
  'dekafinisher':    { name: 'DEKAFINISHER', url: 'https://dekalin.de/produkt/608/', description: 'Oberflächenglättung für MS-Polymer-Dichtstoffe' },
  'dekaclean-ultra': { name: 'DEKACLEAN Ultra Reiniger', url: 'https://dekalin.de/produkt/594/', description: 'Reinigungstücher für Oberflächen vor dem Kleben/Dichten — kein Spray, kein Hautreiniger' },
  'dekavator-250ml': { name: 'DEKAVATOR 250 ml', url: 'https://dekalin.de/produkt/601/', description: 'Haftvermittler für Kunststoffe' },
  'dekavator-nf-30ml': { name: 'DEKAVATOR NF 30 ml', url: 'https://dekalin.de/produkt/591/', description: 'Haftvermittler für MS-Polymer-Produkte' },

  // ── Sets & Buch ───────────────────────────────────────────
  'klebeset-solarmodule': { name: 'Flexsolarmodul Klebeset', url: 'https://dekalin.de/produkt/656/', description: 'Komplettpaket für flexible Solarmodule auf Caravan-Dächern' },
  'klebeset-sat-solar':   { name: 'Klebeset weiß MS-5', url: 'https://dekalin.de/produkt/dekalin-klebeset-solarmodule/', description: 'Komplettpaket für SAT-/Solaranlagen, ohne Bohren' },
  'buch':                 { name: 'Klebt nicht gibt\'s nicht! (Buch)', url: 'https://dekalin.de/produkt/klebt-nicht-gibts-nicht/', description: 'DIY-Leitfaden von Michael Windecker, 150 Seiten' },
};

/**
 * How-to Video URLs — DEKALIN Akademie (gecrawlt 2026-04-02)
 * Source: https://dekalin.de/dekalin-akademie/
 */
export const VIDEO_URLS: Record<string, { name: string; url: string; description: string; category: string[]; relatedProducts: string[] }> = {
  // ── Grundlagen ────────────────────────────────────────────
  'kleben-oder-dichten': {
    name: 'Kleben oder Dichten?',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Kleben-oder-Dichten-am-Wohnmobil_-Die-Profi-Faustformel-von-Dekalin.mp4',
    description: 'Wann ist eine Verklebung sinnvoll und wann reicht eine Abdichtung? Die Profi-Faustformel.',
    category: ['grundlagen'],
    relatedProducts: ['ms-1', 'ms-2', 'ms-5', 'dekaseal-8936', 'dekaseal-1512'],
  },
  'klebe-fehler-vermeiden': {
    name: 'Klebe-Fehler vermeiden',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Klebe-Fehler-vermeiden_-Warum-Klebenormen-am-Wohnmobil-wichtig-sind.mp4',
    description: '80 % aller Fehler passieren vor dem Klebevorgang. Die häufigsten Fehlerquellen.',
    category: ['grundlagen'],
    relatedProducts: ['dekaclean-ultra', 'dekavator-250ml', 'dekavator-nf-30ml'],
  },
  'anwenderberichte': {
    name: 'Anwenderberichte – Warum Camper auf Dekalin vertrauen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Nie_wieder_Wasserschaden__Warum_Camper_beim_Abdichten_auf_Dekalin_vertrauen-1.mp4',
    description: 'Echte Camper, echte Projekte, ehrliche Meinungen.',
    category: ['anwenderberichte'],
    relatedProducts: [],
  },

  // ── Reinigen ──────────────────────────────────────────────
  'klebeflaechen-reinigen': {
    name: 'Wohnmobil Klebeflächen reinigen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Wohnmobil_Klebeflaechen_reinigen__Die_perfekte_Vorbehandlung_mit_Dekalin.mp4',
    description: 'Oberflächen richtig vorbereiten, damit Kleb- und Dichtstoffe dauerhaft haften.',
    category: ['reinigen'],
    relatedProducts: ['dekaclean-ultra', 'dekavator-250ml', 'dekavator-nf-30ml'],
  },

  // ── Kleben & Dichten ──────────────────────────────────────
  'solarpanel-montieren': {
    name: 'Starres Solarpanel montieren',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Solarpanel_auf_Wohnmobil_Dach_kleben__Profi-Guide_mit_Dekasyl_MS-5.mp4',
    description: 'Schritt für Schritt zur sicheren Verklebung eines Solarmoduls auf dem Dach.',
    category: ['kleben-dichten', 'kleben-reparieren'],
    relatedProducts: ['ms-5-weiss', 'ms-5-schwarz', 'klebeset-sat-solar', 'dekaclean-ultra'],
  },
  'flexible-solarmatte': {
    name: 'Flexible Solarmatte befestigen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Flexibles_Solarpanel_auf_Wohnmobil_kleben__So_halten_Solarmatten_dauerhaft.mp4',
    description: 'Was bei gewölbten Dächern zu beachten ist und wie der Randabschluss sauber gelingt.',
    category: ['kleben-dichten'],
    relatedProducts: ['klebeset-solarmodule', 'ms-5-weiss', 'ms-5-schwarz', 'dekaclean-ultra'],
  },
  'sat-antenne-verkleben': {
    name: 'SAT-Antenne verkleben',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/SAT-Antenne_am_Wohnmobil_nachruesten__Sicher_verkleben_ohne_Bohren.mp4',
    description: 'Antenne fest und sicher auf dem Dach anbringen – ohne Bohren.',
    category: ['kleben-dichten', 'dichten'],
    relatedProducts: ['ms-5-weiss', 'ms-5-schwarz', 'klebeset-sat-solar', 'dekaclean-ultra'],
  },
  'maxxfan-einbauen': {
    name: 'MaxxFan Dachlüfter einbauen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Maxxfan_einbauen__Anleitung_zum_Einkleben_und_Abdichten_der_MaxxAir_Dachluke.mp4',
    description: 'Anleitung zum Einkleben und Abdichten der MaxxAir Dachluke.',
    category: ['kleben-dichten'],
    relatedProducts: ['dekaseal-1512', 'dekaseal-1512-cremeweiss', 'dekaseal-1512-schwarz', 'dekaclean-ultra'],
  },
  'dachluke-einbauen': {
    name: 'Dachluke im Wohnmobil einbauen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Dachluke_einbauen_Wohnmobil__Kastenwagen-Anleitung_mit_Dekaseal_1512.mp4',
    description: 'Die entscheidenden Schritte für eine Dachluke, die dauerhaft dicht hält.',
    category: ['kleben-dichten', 'dichten'],
    relatedProducts: ['dekaseal-1512', 'dekaseal-1512-cremeweiss', 'dekaseal-1512-schwarz', 'dekaclean-ultra'],
  },
  'fenster-abdichten': {
    name: 'Eingebautes Fenster abdichten',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Wohnmobil_Fenster_abdichten__Anleitung_mit_Dekaseal_8936_abtupfbar.mp4',
    description: 'Sauber nachgedichtet und überschussfrei – kein Spielraum für Feuchtigkeit.',
    category: ['kleben-dichten', 'dichten', 'kleben-reparieren'],
    relatedProducts: ['dekaseal-8936', 'dekaseal-8936-hellgrau', 'dekaseal-8936-schwarz', 'dekaseal-8936-anthrazit', 'dekaclean-ultra'],
  },
  'undichtes-fenster-abdichten': {
    name: 'Undichtes Camper-Fenster abdichten',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Undichtes_Fenster_am_Camper__Ausbau_und_Neu-Eindichten_Schritt_fuer_Schritt.mp4',
    description: 'Alte Dichtmasse entfernen und das Fenster neu einsetzen – Schritt für Schritt.',
    category: ['kleben-dichten', 'kleben-reparieren'],
    relatedProducts: ['dekaseal-8936', 'dekaseal-8936-hellgrau', 'dekaseal-8936-schwarz', 'dekaclean-ultra'],
  },
  'kuehlschrankgitter-abdichten': {
    name: 'Kühlschrankgitter am Wohnmobil abdichten',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Wohnmobil_Kuehlschrankgitter_abdichten__Anleitung_mit_Dekaseal_8936.mp4',
    description: 'Zuverlässig versiegelt, damit keine Feuchtigkeit hinter die Außenwand zieht.',
    category: ['kleben-dichten', 'dichten'],
    relatedProducts: ['dekaseal-8936', 'dekaseal-8936-hellgrau', 'dekaseal-8936-schwarz', 'dekaclean-ultra'],
  },
  'seitenfenster-einbauen': {
    name: 'Seitenfenster in Camper einbauen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Van_Ausbau__Fenster_in_Seitenwand_einbauen_und_einkleben_Anleitung.mp4',
    description: 'Von der Wandöffnung bis zum Rostschutz – alles für ein sauber eingesetztes Fenster.',
    category: ['kleben-dichten', 'dichten'],
    relatedProducts: ['dekaseal-1512', 'dekaseal-1512-cremeweiss', 'dekaseal-1512-schwarz', 'ms-2', 'dekaclean-ultra'],
  },
  'serviceklappe-einbauen': {
    name: 'Serviceklappe im Wohnmobil einbauen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Serviceklappe_Wohnmobil_nachruesten__Anleitung_zum_Einkleben_und_Abdichten.mp4',
    description: 'Rahmen einkleben, Öffnung abdichten – Schritt für Schritt zum sauberen Einbau.',
    category: ['kleben-dichten'],
    relatedProducts: ['dekaseal-8936', 'ms-1', 'ms-2', 'dekaclean-ultra'],
  },
  'grosse-serviceklappe-nachruesten': {
    name: 'Große Serviceklappe nachrüsten',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Wohnmobil_Serviceklappe_nachruesten__Anleitung_zum_Verstaerken_und_Einkleben.mp4',
    description: 'Verstärkung der Fahrzeugwand, stabiler Einbau und eine Abdichtung, die hält.',
    category: ['kleben-dichten', 'kleben-reparieren'],
    relatedProducts: ['dekaseal-8936', 'ms-1', 'ms-2', 'dekaclean-ultra'],
  },
  'aussensteckdose-einbauen': {
    name: 'Außensteckdose am Wohnmobil einbauen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Aussensteckdose_am_Wohnmobil_nachruesten__So_wird_sie_sicher_abdichtet.mp4',
    description: 'Sicher eingesetzt, rundum wasserdicht.',
    category: ['kleben-dichten', 'dichten'],
    relatedProducts: ['dekaseal-8936', 'dekaseal-8936-schwarz', 'dekaclean-ultra'],
  },
  'markise-befestigen': {
    name: 'Wohnmobil Markise befestigen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Wohnmobil_Markise_anbauen_und_abdichten__Profi-Anleitung_der_Dekalin_Akademie.mp4',
    description: 'Halterung, Abdichtung, sichere Montage an der Fahrzeugwand.',
    category: ['kleben-dichten', 'kleben-reparieren'],
    relatedProducts: ['dekaseal-8936', 'dekaseal-8936-schwarz', 'ms-1', 'dekaclean-ultra'],
  },
  'fugen-ziehen': {
    name: 'Fugen am Wohnmobil ziehen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Wohnmobil_Fugen_ziehen__Profi-Anleitung_fuer_saubere_und_dichte_Fugen.mp4',
    description: 'So gelingt eine Fuge, die ordentlich aussieht und hält, was sie verspricht.',
    category: ['kleben-dichten', 'dichten', 'schuetzen', 'kleben-reparieren'],
    relatedProducts: ['ms-1', 'ms-1-weiss', 'ms-1-grau', 'ms-7', 'dekafinisher', 'dekaclean-ultra'],
  },

  // ── Schützen ──────────────────────────────────────────────
  'hohlraumversiegelung': {
    name: 'Hohlraumversiegelung selber machen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Wohnmobil_Hohlraumversiegelung_selber_machen__Rostschutz-Anleitung.mp4',
    description: 'Karosserie von innen schützen – von der Vorbereitung bis zur Versiegelung.',
    category: ['schuetzen'],
    relatedProducts: ['dekaphon-pro-wax'],
  },
  'unterbodenschutz-spruehen': {
    name: 'Unterbodenschutz aufsprühen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Wohnmobil_Unterbodenschutz_aufspruehen__Profi-Tipps_fuer_perfekten_Rostschutz.mp4',
    description: 'Gleichmäßige Schutzschicht aufsprühen – mit Vorbereitung.',
    category: ['schuetzen'],
    relatedProducts: ['dekaphon-9735', 'dekaphon-958'],
  },
  'unterbodenschutz-rollen': {
    name: 'Unterbodenschutz aufrollen & pinseln',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Wohnmobil_Unterbodenschutz_auftragen__Anleitung_zum_Aufrollen_und_Pinseln.mp4',
    description: 'Die Alternative zum Sprühen – mehr Kontrolle, genauso wirkungsvoll.',
    category: ['schuetzen'],
    relatedProducts: ['dekaphon-9735', 'dekaphon-958'],
  },

  // ── Kleben & Reparieren ───────────────────────────────────
  'dachfenster-ausbauen': {
    name: 'Wohnmobil Dachfenster ausbauen',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Wohnmobil_Dachfenster_ausbauen__Anleitung_zur_Demontage_und_Reinigung.mp4',
    description: 'Dachfenster sauber ausbauen – die Grundlage für jeden Austausch oder jede Reparatur.',
    category: ['kleben-reparieren'],
    relatedProducts: ['dekaclean-ultra', 'dekaseal-1512', 'dekaseal-8936'],
  },
  'kederleiste-abdichten': {
    name: 'Kederleiste am Wohnmobil abdichten',
    url: 'https://dekalin.de/wp-content/uploads/2026/03/Kederleiste_am_Wohnmobil_abdichten__So_bleibt_dein_Wohnmobil_trocken.mp4',
    description: 'Undichte oder beschädigte Kederleiste abdichten und dauerhaft versiegeln.',
    category: ['kleben-reparieren'],
    relatedProducts: ['dekaseal-8936', 'dekaseal-8936-hellgrau', 'dekaseal-8936-schwarz', 'dekaclean-ultra'],
  },
};

/**
 * Blog/Ratgeber URLs — Echte dekalin.de URLs (gecrawlt 2026-03-17)
 * URL-Pattern: /ratgeber/{slug}/ und /ratgeber/{parent}/{child}/
 * SSOT: 05-wissensbasis/dekalin-url-mapping.md
 */
export const BLOG_URLS: Record<string, { name: string; url: string }> = {
  // ── Ratgeber ──────────────────────────────────────────────
  'wasserschaden':           { name: 'Ratgeber: Wasserschaden selbst reparieren', url: 'https://dekalin.de/ratgeber/wasserschaden-selbst-reparieren/' },
  'do-it-yourself':          { name: 'Ratgeber: Do it yourself!', url: 'https://dekalin.de/ratgeber/do-it-yourself/' },
  'richtig-kleben':          { name: 'Ratgeber: Richtig Kleben und Dichten', url: 'https://dekalin.de/ratgeber/richtig-kleben-und-dichten/' },
  'abtupfbare-dichtmasse':   { name: 'Ratgeber: Abtupfbare Dichtmasse', url: 'https://dekalin.de/ratgeber/richtig-kleben-und-dichten/abtupfbare-dichtmasse/' },
  'dekaseal-vergleich':      { name: 'Ratgeber: DEKASEAL 8936 vs. 1512', url: 'https://dekalin.de/ratgeber/richtig-kleben-und-dichten/dekaseal-8936-vs-dekaseal-1512/' },
  'unterbodenschutz':        { name: 'Ratgeber: Unterbodenschutz', url: 'https://dekalin.de/ratgeber/unterbodenschutz/' },
  'unterbodenschutz-wohnwagen': { name: 'Ratgeber: Unterbodenschutz Wohnwagen', url: 'https://dekalin.de/ratgeber/unterbodenschutz-wohnwagen/' },
  'unterbodenschutz-wohnmobil': { name: 'Ratgeber: Unterbodenschutz Wohnmobil', url: 'https://dekalin.de/ratgeber/unterbodenschutz-wohnmobil/' },
  'markise-anbauteile':      { name: 'Ratgeber: Markise & Anbauteile kleben', url: 'https://dekalin.de/ratgeber/markise-dachtraeger-anbauteile-kleben/' },
  'maxxfan-klimaanlage':     { name: 'Ratgeber: MaxxFan vs. Klimaanlage', url: 'https://dekalin.de/ratgeber/maxxfan-maxxair-vs-klimaanlage/' },
  'buch-ratgeber':           { name: 'Ratgeber: Buch "Klebt nicht gibt\'s nicht!"', url: 'https://dekalin.de/ratgeber/buch-klebt-nicht-gibts-nicht/' },

  // ── Branchen-Seiten ───────────────────────────────────────
  'branche-caravaning':      { name: 'Branche: Caravaning', url: 'https://dekalin.de/branchen/caravaning/' },
  'branche-raumausstatter':  { name: 'Branche: Raumausstatter', url: 'https://dekalin.de/branchen/raumausstatter/' },
  'branche-klima':           { name: 'Branche: Klima & Lüftung', url: 'https://dekalin.de/branchen/klima-und-lueftung/' },
  'branche-sattlereien':     { name: 'Branche: Sattlereien', url: 'https://dekalin.de/branchen/sattlereien/' },
  'branche-industrie':       { name: 'Branche: Industrie', url: 'https://dekalin.de/branchen/industrie/' },

  // ── Ausgewählte Blog-Posts (produktrelevant) ──────────────
  'ms-unterschied':          { name: 'Blog: MS-1 vs. MS-2 vs. MS-5 – der Unterschied', url: 'https://dekalin.de/dekasyl-ms-1-dekasyl-ms-2-dekasyl-ms-5-was-ist-der-unterschied/' },
  'dichten-vs-kleben':       { name: 'Blog: Dichten sinnvoller als Kleben?', url: 'https://dekalin.de/dichten-sinnvoller-als-kleben/' },
  'gut-gereinigt':           { name: 'Blog: Gut gereinigt ist halb gewonnen', url: 'https://dekalin.de/gut-gereinigt-ist-halb-gewonnen/' },
  'isocyanatfrei':           { name: 'Blog: Verzicht auf Isocyanate', url: 'https://dekalin.de/verzicht-auf-isocyanate/' },

  // ── Closing-Flow (Prompt v5.0.7) ──────────────────────────
  'newsletter':              { name: 'Dekalin Newsletter abonnieren', url: 'https://dekalin.de/newsletter' },
  'google-review':           { name: 'Dekalin auf Google bewerten', url: 'https://g.page/r/CVe0jlsp98ynEAE/review' },
};

// ============================================================
// Name-Matching Engine
// ============================================================

/**
 * Lookup-Tabelle: Produktname (lowercase) → { key, entry }
 * Sortiert nach Namenslänge (längste zuerst), damit "DEKASEAL 8936 hellgrau"
 * vor "DEKASEAL 8936" gematcht wird.
 */
interface NameLookup {
  pattern: RegExp;
  key: string;
  entry: { name: string; url: string; description?: string };
}

/**
 * Kurzformen die der Avatar häufig nutzt statt der vollen Produktnamen.
 * Alias → Key in PRODUCT_URLS. Werden zusätzlich zu den vollen Namen gematcht.
 */
const PRODUCT_ALIASES: Record<string, string> = {
  // MS-Serie: Avatar sagt "MS-5" statt "DEKASYL MS-5"
  'MS-1': 'ms-1',
  'MS-2': 'ms-2',
  'MS-5': 'ms-5',
  'MS-6': 'ms-6-weiss',
  'MS-7': 'ms-7',
  'MS-8': 'ms-8-schwarz',
  // Kurzformen ohne Suffix
  'DEKACLEAN Ultra': 'dekaclean-ultra',
  'DEKACLEAN': 'dekaclean-ultra',
  'PLASTIGUM 77 TF': 'plastigum-77-tf',
  'PLASTIGUM': 'plastigum-77-tf',
  'DEKAVATOR': 'dekavator-250ml',
  'DEKAVATOR NF': 'dekavator-nf-30ml',
  'DEKAVATOR NF 30 ml': 'dekavator-nf-30ml',
  'DEKAVATOR 22 NF': 'dekavator-nf-30ml',
  'DEKAPUR 2K-6000': 'dekapur-2k-6000',
  'DEKAPUR 2K-90': 'dekapur-2k-90',
  'DEKAPREN 3649': 'dekapren-3649',
  'DEKASYL 240': 'dekasyl-240-grau',
  // Phonetische Varianten (Prompt v4.0 – TTS-Aussprache)
  'Dekasiel acht-neun-drei-sechs': 'dekaseal-8936',
  'Dekasiel acht-neun-drei-neun': 'dekaseal-8939',
  'Dekasiel eins-fünf-eins-zwei': 'dekaseal-1512',
  'Dekasiel acht-acht-sechs-neun': 'dekaseal-8869',
  'DEKASEAL acht-neun-drei-sechs': 'dekaseal-8936',
  'DEKASEAL acht-neun-drei-neun': 'dekaseal-8939',
  'DEKASEAL eins-fünf-eins-zwei': 'dekaseal-1512',
  'DEKASEAL acht-acht-sechs-neun': 'dekaseal-8869',
  'Dekasüll M-S-eins': 'ms-1',
  'Dekasüll M-S-zwei': 'ms-2',
  'Dekasüll M-S-fünf': 'ms-5',
  'Dekasüll M-S-sechs': 'ms-6-weiss',
  'Dekasüll M-S-sieben': 'ms-7',
  'Dekasüll M-S-acht': 'ms-8-schwarz',
  'Dekaklihn neun-neun-vier-null': 'dekaclean-9940',
  'Dekaklihn Ultra': 'dekaclean-ultra',
  'Dekafon neun-sieben-drei-fünf': 'dekaphon-9735',
  'Dekafon neun-fünf-acht': 'dekaphon-958',
  'Dekawator': 'dekavator-250ml',
  'Dekafinischer': 'dekafinisher',
  'Dekaplast sieben-drei-null-drei': 'dekaplast-7303',
  'Dekapren drei-sechs-vier-neun': 'dekapren-3649',
  'Plastigumm': 'plastigum-77-tf',
  'Dekapur zwei-K-sechs-tausend': 'dekapur-2k-6000',
  'Dekapur zwei-K-neunzig': 'dekapur-2k-90',
  'Dekafon Pro-Vaks': 'dekaphon-pro-wax',
  'Dekawator zwei-zwei N-F': 'dekavator-nf-30ml',
  'Dekawator N-F': 'dekavator-nf-30ml',
  'Plastigumm sieben-sieben T-F': 'plastigum-77-tf',
  'Dekasüll zwei-vierzig': 'dekasyl-240-grau',
  // Partial phonetic (M-S-X)
  'M-S-eins': 'ms-1',
  'M-S-zwei': 'ms-2',
  'M-S-fünf': 'ms-5',
  'M-S-sechs': 'ms-6-weiss',
  'M-S-sieben': 'ms-7',
  'M-S-acht': 'ms-8-schwarz',
};

const PRODUCT_NAME_LOOKUPS: NameLookup[] = buildNameLookups(PRODUCT_URLS);
const BLOG_KEYWORD_RULES = buildBlogKeywordRules();

function buildNameLookups(
  urls: Record<string, { name: string; url: string; description?: string }>,
): NameLookup[] {
  // Dedupliziere nach Name (gleicher Name = gleicher Match, nutze ersten Key)
  const byName = new Map<string, { key: string; entry: (typeof urls)[string] }>();
  for (const [key, entry] of Object.entries(urls)) {
    const nameLower = entry.name.toLowerCase();
    if (!byName.has(nameLower)) {
      byName.set(nameLower, { key, entry });
    }
  }

  // Aliase hinzufügen (Kurzformen die der Avatar häufig nutzt)
  for (const [alias, key] of Object.entries(PRODUCT_ALIASES)) {
    const aliasLower = alias.toLowerCase();
    if (!byName.has(aliasLower) && urls[key]) {
      byName.set(aliasLower, { key, entry: urls[key] });
    }
  }

  // Sortiere nach Namenslänge (längste zuerst)
  return Array.from(byName.entries())
    .sort((a, b) => b[0].length - a[0].length)
    .map(([nameLower, { key, entry }]) => ({
      pattern: new RegExp(escapeRegex(nameLower), 'i'),
      key,
      entry,
    }));
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Blog-Erkennung per Keywords.
 * Jede Regel hat ein Set von Keywords — wenn ALLE im Text vorkommen, wird der Blog gematcht.
 */
interface BlogKeywordRule {
  key: string;
  keywords: string[];
}

function buildBlogKeywordRules(): BlogKeywordRule[] {
  return [
    // Ratgeber — spezifisch (höhere Priorität, oben)
    { key: 'dekaseal-vergleich', keywords: ['8936', '1512'] },
    { key: 'ms-unterschied', keywords: ['ms-1', 'ms-2'] },
    { key: 'abtupfbare-dichtmasse', keywords: ['abtupfbar'] },
    { key: 'unterbodenschutz-wohnmobil', keywords: ['unterbodenschutz', 'wohnmobil'] },
    { key: 'unterbodenschutz-wohnwagen', keywords: ['unterbodenschutz', 'wohnwagen'] },
    { key: 'unterbodenschutz', keywords: ['unterbodenschutz'] },
    { key: 'markise-anbauteile', keywords: ['markise'] },
    { key: 'maxxfan-klimaanlage', keywords: ['maxxfan'] },
    { key: 'maxxfan-klimaanlage', keywords: ['klimaanlage', 'lüftung'] },
    { key: 'wasserschaden', keywords: ['wasserschaden'] },
    { key: 'wasserschaden', keywords: ['wasser', 'reparier'] },
    { key: 'buch-ratgeber', keywords: ['buch'] },

    // Ratgeber — allgemeine Themen (keywords müssen spezifisch genug sein um Begrüßung nicht zu matchen)
    { key: 'richtig-kleben', keywords: ['richtig kleben'] },
    { key: 'richtig-kleben', keywords: ['ratgeber', 'kleben'] },
    { key: 'richtig-kleben', keywords: ['anleitung', 'kleben'] },
    { key: 'do-it-yourself', keywords: ['selbst machen'] },
    { key: 'do-it-yourself', keywords: ['diy'] },
    { key: 'do-it-yourself', keywords: ['do it yourself'] },

    // Blog-Posts
    { key: 'dichten-vs-kleben', keywords: ['dichten', 'kleben', 'unterschied'] },
    { key: 'dichten-vs-kleben', keywords: ['dichten', 'sinnvoller'] },
    { key: 'gut-gereinigt', keywords: ['reinig', 'vorbereitung'] },
    { key: 'gut-gereinigt', keywords: ['reinig', 'haftung'] },
    { key: 'isocyanatfrei', keywords: ['isocyanat'] },

    // Branchen
    { key: 'branche-caravaning', keywords: ['caravan', 'branche'] },
    { key: 'branche-raumausstatter', keywords: ['raumausstatt'] },
    { key: 'branche-klima', keywords: ['klima', 'lüftung', 'branche'] },
    { key: 'branche-sattlereien', keywords: ['sattler'] },
    { key: 'branche-industrie', keywords: ['industrie', 'branche'] },

    // Closing-Flow (Prompt v5.0.7) — Newsletter / Google-Review
    { key: 'newsletter', keywords: ['newsletter'] },
    { key: 'newsletter', keywords: ['auf dem laufenden'] },
    { key: 'google-review', keywords: ['google', 'bewertung'] },
    { key: 'google-review', keywords: ['google', 'review'] },
  ];
}

// ============================================================
// Negation-Detection — verhindert Produkt-Cards wenn Brian
// ein Produkt NEGATIV erwähnt (z.B. "DEKAVATOR ist hier kein Lösungsansatz")
// Hintergrund: Brian sagt im Prompt v5.0.5 explizit dass DEKAVATOR
// NICHT für PE/PP/PTFE/PMMA/Polycarbonat hilft. Ohne diesen Check würde
// die Card-Logik DEKAVATOR trotzdem als Empfehlung anzeigen.
// ============================================================

/**
 * Negative Kontext-Wörter/-Phrasen, die anzeigen dass ein Produkt
 * NICHT empfohlen wird. Werden in einem Fenster von ±70 Zeichen um
 * jeden Produkt-Match geprüft.
 */
const NEGATION_PATTERNS: RegExp[] = [
  // "nicht ..."
  /\bnicht (kompatibel|geeignet|passend|empfohlen|möglich|hilfreich|sinnvoll|im sortiment|mehr.*sortiment)\b/i,
  // "kein ..."
  /\bkein (lösungsansatz|lösungsweg|sinn|primer|weg|standard.?produkt|ersatz)\b/i,
  // "keine ..."
  /\bkeine (empfehlung|haftung|hilfe|lösung|option|haftwirkung|wahl)\b/i,
  // "macht ... nicht ..."
  /\bmacht.{0,20}nicht.{0,20}(kompatibel|haften|hilfreich|möglich)\b/i,
  // "ist hier kein ..."
  /\bist (hier )?kein\b/i,
  // "kommen ... nicht weiter"
  /\bkommen .{0,30}nicht weiter\b/i,
  // "aktuell nicht im Sortiment"
  /\baktuell nicht im sortiment\b/i,
  // "nicht mehr im Sortiment"
  /\bnicht mehr im sortiment\b/i,
  // "B2B-Spezialprodukt"  — nicht für Endkunden
  /\bb2b.?spezialprodukt\b/i,
  // "nicht im Endkunden-Sortiment"
  /\bnicht im endkunden.?sortiment\b/i,
];

/**
 * Prüft ob ein Produkt-Match in einem negativen Kontext steht.
 * Schaut ±70 Zeichen um die Match-Position.
 */
function isNegativeContext(text: string, matchIndex: number, matchLength: number): boolean {
  const contextStart = Math.max(0, matchIndex - 70);
  const contextEnd = Math.min(text.length, matchIndex + matchLength + 70);
  const context = text.slice(contextStart, contextEnd);
  return NEGATION_PATTERNS.some(p => p.test(context));
}

// Handoff-Erkennung: Marker [HANDOFF] ODER natürliche Übergabe-Phrasen
const HANDOFF_MARKER_REGEX = /\[HANDOFF\]/gi;
const HANDOFF_PHRASE_PATTERNS = [
  /verbinde dich mit.*(berater|kundendienst|kollege|fachberater|dekalin)/i,
  /verbinde ich dich/i,
  /verbinde ich sie/i,
  /übergebe.*(berater|kundendienst|mensch|fachberater)/i,
  /leite.*weiter.*kundendienst/i,
  // Sicherheits-Trigger (v5.0): Bot sagt "Stopp — Sicherheitsthema" bei Gefahrensituationen
  /stopp.*sicherheitsthema/i,
  /das ist ein sicherheitsthema/i,
  /gehört in eine fachwerkstatt/i,
  /giftnotruf/i,
];

/**
 * Erkennt Produkte und Blogs im Avatar-Text per Name-Matching.
 * Keine Marker nötig — scannt den Text nach bekannten Produktnamen.
 *
 * @param avatarText - Text aus dem avatar.transcription-Event
 * @returns ParseResult mit Text, erkannten Produkten, Blogs und Handoff-Flag
 */
export function parseAvatarText(avatarText: string): ParseResult {
  // Handoff: check marker OR natural phrases
  HANDOFF_MARKER_REGEX.lastIndex = 0;
  const hasMarker = HANDOFF_MARKER_REGEX.test(avatarText);
  const hasPhrase = HANDOFF_PHRASE_PATTERNS.some(p => p.test(avatarText));
  const hasHandoff = hasMarker || hasPhrase;

  // Produkte per Name-Matching erkennen
  // Skip wenn Produkt in negativem Kontext erwähnt wird
  // (z.B. "DEKAVATOR ist hier kein Lösungsansatz" → keine Card)
  const products: ProductCard[] = [];
  const matchedKeys = new Set<string>();

  for (const lookup of PRODUCT_NAME_LOOKUPS) {
    if (matchedKeys.has(lookup.key)) continue;
    const match = lookup.pattern.exec(avatarText);
    if (match && match.index !== undefined) {
      // Negation-Check: Produkt im negativen Kontext? → Skip
      if (isNegativeContext(avatarText, match.index, match[0].length)) {
        continue;
      }
      matchedKeys.add(lookup.key);
      products.push({
        key: lookup.key,
        name: lookup.entry.name,
        url: lookup.entry.url,
        category: 'produkt',
        description: lookup.entry.description,
      });
    }
  }

  // Videos per relatedProducts-Matching — zeige Videos zu erkannten Produkten (max. 2)
  const videos: ProductCard[] = [];
  const matchedVideoKeys = new Set<string>();
  const textLower = avatarText.toLowerCase();

  for (const [key, entry] of Object.entries(VIDEO_URLS)) {
    if (matchedVideoKeys.has(key)) continue;
    if (entry.relatedProducts.some(pk => matchedKeys.has(pk))) {
      matchedVideoKeys.add(key);
      videos.push({ key, name: entry.name, url: entry.url, category: 'video', description: entry.description });
      if (videos.length >= 2) break;
    }
  }

  // Blogs per Keyword-Matching erkennen (dedupliziert nach Key)
  const blogs: ProductCard[] = [];
  const matchedBlogKeys = new Set<string>();

  for (const rule of BLOG_KEYWORD_RULES) {
    if (matchedBlogKeys.has(rule.key)) continue;
    if (rule.keywords.every(kw => textLower.includes(kw))) {
      const blog = BLOG_URLS[rule.key];
      if (blog) {
        matchedBlogKeys.add(rule.key);
        blogs.push({
          key: rule.key,
          name: blog.name,
          url: blog.url,
          category: 'blog',
        });
      }
    }
  }

  // Handoff-Marker aus Text entfernen (einziger verbleibender Marker)
  HANDOFF_MARKER_REGEX.lastIndex = 0;
  const cleanText = avatarText
    .replace(HANDOFF_MARKER_REGEX, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return { cleanText, products, blogs, videos, hasHandoff };
}

/**
 * Dedupliziert Produktkarten (gleicher Key nur einmal anzeigen).
 */
export function deduplicateCards(cards: ProductCard[]): ProductCard[] {
  const seen = new Set<string>();
  return cards.filter((card) => {
    if (seen.has(card.key)) return false;
    seen.add(card.key);
    return true;
  });
}
